import { BtrPassportModel } from '@app/core/models/btr-passport.model';
import { PassportBeaconGroup, VerificationTier } from '@app/core/models/btr-event.model';
import { IBmTicketStatus } from '@app/core/models/api';
import { TicketModel } from '@app/core/models/ticket.model';

const DEFAULT_TIER_TITLE = 'Complete';

export interface PassportGroupProgress {
  group: PassportBeaconGroup;
  visitedCount: number;
  remainingCount: number;
  isCompleted: boolean;
}

export const PassportProgressHelper = {
  getGroups(
    passport: Pick<BtrPassportModel, 'passportBeaconGroups' | 'tickets'>,
    ticket?: TicketModel,
  ): Array<PassportBeaconGroup> {
    return ticket?.passportBeaconGroups?.length
      ? ticket.passportBeaconGroups
      : passport?.passportBeaconGroups ?? [];
  },

  getTiers(
    passport: Pick<BtrPassportModel, 'verificationTiers' | 'tickets'>,
    ticket?: TicketModel,
  ): Array<VerificationTier> {
    return ticket?.verificationTiers?.length
      ? ticket.verificationTiers
      : passport?.verificationTiers ?? [];
  },

  getVisitedBeaconIds(status?: IBmTicketStatus): Array<number> {
    return [...new Set(status?.verifications?.map(item => item.beacon_id) ?? [])];
  },

  getVisitedCount(status?: IBmTicketStatus): number {
    return PassportProgressHelper.getVisitedBeaconIds(status).length;
  },

  getVerifiedTierIds(status?: IBmTicketStatus): Array<number> {
    if (Array.isArray(status?.verified_tiers)) {
      return [...new Set(status.verified_tiers.map(item => item.id))];
    }

    return status?.verification_tiers?.id != null
      ? [status.verification_tiers.id]
      : [];
  },

  isTierVerified(status: IBmTicketStatus | undefined, tierId: number): boolean {
    return PassportProgressHelper.getVerifiedTierIds(status).includes(tierId);
  },

  getVerifiedTiers(
    passport: Pick<BtrPassportModel, 'verificationTiers' | 'places' | 'tickets'>,
    status?: IBmTicketStatus,
    ticket?: TicketModel,
  ): Array<VerificationTier> {
    const verifiedTierIds = PassportProgressHelper.getVerifiedTierIds(status);

    return PassportProgressHelper.getSortedTiers(passport, ticket).filter(tier =>
      verifiedTierIds.includes(tier.id),
    );
  },

  getCompletionThreshold(
    passport: Pick<BtrPassportModel, 'verificationTiers' | 'places' | 'tickets'>,
    ticket?: TicketModel,
  ): number {
    return (
      PassportProgressHelper.getTopTier(passport, ticket)?.threshold ??
      ticket?.cBeaconsToVerify ??
      passport?.tickets?.[0]?.cBeaconsToVerify ??
      passport?.places?.length ??
      0
    );
  },

  getSortedTiers(
    passport: Pick<BtrPassportModel, 'verificationTiers' | 'places' | 'tickets'>,
    ticket?: TicketModel,
  ): Array<VerificationTier> {
    const tiers = [...PassportProgressHelper.getTiers(passport, ticket)].sort(
      (left, right) => left.threshold - right.threshold,
    );

    if (tiers.length > 0) {
      return tiers;
    }

    return [
      {
        id: 0,
        title: DEFAULT_TIER_TITLE,
        threshold: PassportProgressHelper.getCompletionThresholdFallback(
          passport,
          ticket,
        ),
        description: '',
        thresholdGroups: 0,
        isVerifyTicket: true,
      },
    ];
  },

  getTopTier(
    passport: Pick<BtrPassportModel, 'verificationTiers' | 'places' | 'tickets'>,
    ticket?: TicketModel,
  ): VerificationTier | null {
    const tiers = PassportProgressHelper.getSortedTiers(passport, ticket);
    return tiers.length > 0 ? tiers[tiers.length - 1] : null;
  },

  isMultiTierPassport(
    passport: Pick<BtrPassportModel, 'verificationTiers' | 'places' | 'tickets'>,
    ticket?: TicketModel,
  ): boolean {
    return PassportProgressHelper.getSortedTiers(passport, ticket).length > 1;
  },

  getCurrentTier(
    passport: Pick<BtrPassportModel, 'verificationTiers' | 'places' | 'tickets'>,
    status?: IBmTicketStatus,
    ticket?: TicketModel,
  ): VerificationTier | null {
    const tiers = PassportProgressHelper.getVerifiedTiers(
      passport,
      status,
      ticket,
    );

    return tiers.length > 0 ? tiers[tiers.length - 1] : null;
  },

  getNextTier(
    passport: Pick<BtrPassportModel, 'verificationTiers' | 'places' | 'tickets'>,
    status?: IBmTicketStatus,
    ticket?: TicketModel,
  ): VerificationTier | null {
    const currentTier = PassportProgressHelper.getCurrentTier(
      passport,
      status,
      ticket,
    );
    const verifiedTierIds = PassportProgressHelper.getVerifiedTierIds(status);

    return (
      PassportProgressHelper.getSortedTiers(passport, ticket).find(
        tier =>
          !verifiedTierIds.includes(tier.id) &&
          (currentTier == null || tier.threshold > currentTier.threshold),
      ) ?? null
    );
  },

  getGroupProgress(
    passport: Pick<BtrPassportModel, 'passportBeaconGroups' | 'tickets'>,
    status?: IBmTicketStatus,
    ticket?: TicketModel,
  ): Array<PassportGroupProgress> {
    const visitedIds = PassportProgressHelper.getVisitedBeaconIds(status);

    return PassportProgressHelper.getGroups(passport, ticket).map(group => {
      const visitedCount = group.beaconIds.filter(id =>
        visitedIds.includes(id),
      ).length;

      return {
        group,
        visitedCount,
        remainingCount: Math.max(group.threshold - visitedCount, 0),
        isCompleted: visitedCount >= group.threshold,
      };
    });
  },

  getCompletedGroupsCount(
    passport: Pick<BtrPassportModel, 'passportBeaconGroups' | 'tickets'>,
    status?: IBmTicketStatus,
    ticket?: TicketModel,
  ): number {
    return PassportProgressHelper.getGroupProgress(
      passport,
      status,
      ticket,
    ).filter(item => item.isCompleted).length;
  },

  getRequiredCompletionGroups(
    passport: Pick<
      BtrPassportModel,
      'verificationTiers' | 'passportBeaconGroups' | 'places' | 'tickets'
    >,
    ticket?: TicketModel,
  ): number {
    const groups = PassportProgressHelper.getGroups(passport, ticket);
    if (groups.length === 0) {
      return 0;
    }

    return groups.length;
  },

  areCompletionGroupsSatisfied(
    passport: Pick<
      BtrPassportModel,
      'verificationTiers' | 'passportBeaconGroups' | 'places' | 'tickets'
    >,
    status?: IBmTicketStatus,
    ticket?: TicketModel,
  ): boolean {
    const requiredGroups = PassportProgressHelper.getRequiredCompletionGroups(
      passport,
      ticket,
    );

    if (requiredGroups === 0) {
      return true;
    }

    return PassportProgressHelper.getGroupProgress(passport, status, ticket)
      .slice(0, requiredGroups)
      .every(item => item.isCompleted);
  },

  isPassportCompleted(
    passport: Pick<
      BtrPassportModel,
      'verificationTiers' | 'passportBeaconGroups' | 'places' | 'tickets'
    >,
    status?: IBmTicketStatus,
    ticket?: TicketModel,
  ): boolean {
    return (
      PassportProgressHelper.getVisitedCount(status) >=
        PassportProgressHelper.getCompletionThreshold(passport, ticket) &&
      PassportProgressHelper.areCompletionGroupsSatisfied(
        passport,
        status,
        ticket,
      )
    );
  },

  isAllStopsVisited(
    passport: Pick<BtrPassportModel, 'places'>,
    status?: IBmTicketStatus,
  ): boolean {
    return (
      PassportProgressHelper.getVisitedCount(status) >=
      (passport?.places?.length ?? 0)
    );
  },

  didCompleteOnCurrentCheckIn(
    passport: Pick<
      BtrPassportModel,
      'verificationTiers' | 'passportBeaconGroups' | 'places' | 'tickets'
    >,
    status: IBmTicketStatus,
    currentPlaceId: number,
    ticket?: TicketModel,
  ): boolean {
    if (!status || !currentPlaceId) {
      return false;
    }

    return (
      PassportProgressHelper.isPassportCompleted(passport, status, ticket) &&
      !PassportProgressHelper.isPassportCompleted(
        passport,
        PassportProgressHelper.getStatusWithoutPlace(status, currentPlaceId),
        ticket,
      )
    );
  },

  getStatusWithoutPlace(
    status: IBmTicketStatus,
    placeId: number,
  ): IBmTicketStatus {
    return {
      ...status,
      verifications:
        status?.verifications?.filter(
          verification => verification.beacon_id !== placeId,
        ) ?? [],
    };
  },

  getCompletionThresholdFallback(
    passport: Pick<BtrPassportModel, 'places' | 'tickets'>,
    ticket?: TicketModel,
  ): number {
    return (
      ticket?.cBeaconsToVerify ??
      passport?.tickets?.[0]?.cBeaconsToVerify ??
      passport?.places?.length ??
      0
    );
  },
};
